import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  cardClasses,
  cn,
  DateField,
  Field,
  Icon,
  linkTextClasses,
  Modal,
  ModalActions,
  NotProvided,
  Pagination,
  Select,
  useOverflow,
} from '@evogard/ui';
import {
  CAREER_PLAN_TIERS,
  countsActivations,
  formatPercentage,
  loadFailureMessage,
} from '@evogard/domain';
import type {
  Award,
  AwardTemplate,
  AwardBasisSource,
  AwardFamily,
  AwardProductCombination,
  CatalogVehicleFilter,
  CatalogVehicleFilters,
  Consultant,
} from '@evogard/contract';
import { MoneyValue } from '../../components/Values.js';
import { api } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { refusalOf, useConsultants } from './common.js';
import {
  BoletoTypePicker,
  ProductPicker,
  type BoletoTypeChoice,
  type ProductChoice,
} from './AwardCatalogPickers.js';

/** O nome da família no cartão e nas listas. */
export function familyLabel(family: AwardFamily): string {
  return family === 'direta'
    ? 'direta'
    : family === 'antecipada'
      ? 'antecipada plano de carreira'
      : family === 'produto'
        ? 'por produto'
        : 'recorrência';
}

/**
 * As premiações DE UM CONSULTOR — a seção dentro de "Editar" no cadastro dele.
 *
 * ## O cadastro foi invertido em 16/09/2026
 *
 * Havia uma tela `Comercial › Premiações`: criava-se a régua, pendurava-se nela
 * quem recebe, salvava. O responsável inverteu — abre-se o consultor, e as
 * premiações dele se definem aqui, com o mesmo período e as mesmas regras.
 *
 * A tela antiga saiu do menu. O que ela tinha e não cabia dentro de uma pessoa
 * — a taxa por boleto, que era da praça inteira — foi para
 * `Configurações › Comercial`, e de lá saiu em 21/09/2026: a tarifa passou a
 * vir do espelho do boleto, e ninguém a digita.
 *
 * ## Uma premiação é de UMA pessoa, e o preço está dito na tela
 *
 * Cinco consultores com a mesma régua são cinco cadastros, e mudar a régua de
 * todos são cinco edições. Em troca, cada um tem período, faixas e prazo
 * próprios sem que mexer no dele toque em ninguém.
 *
 * ## NADA recusa sobreposição, e isso também é dito
 *
 * Duas premiações da mesma pessoa cobrindo os mesmos contratos **pagam as
 * duas**. O banco não recusa mais (as duas travas saíram na mesma data), e o
 * serviço não recusa por escrito — uma régua que sobrevive na camada de cima é
 * a régua ainda de pé. Quem cadastra é quem decide, e a nota abaixo da lista
 * diz isso onde a decisão é tomada.
 *
 * ## Os DOIS lugares onde ela abre são modais de 672px
 *
 * `ConsultantsScreen` a desenha dentro do editor do vínculo e
 * `ManagementPanelScreen` dentro do modal do painel — os dois `size="lg"`. Nada
 * aqui pode decidir forma pelo tamanho da JANELA: nenhum dos dois contextos é
 * largo, e foi assim que a pilha nasceu partida em duas colunas de 320px, com
 * metade da largura vazia quando há uma premiação só. A régua da seção é uma
 * coluna, e a lista rola por dentro; o detalhe está no bloco da listagem.
 */
/**
 * Só o `id` do vínculo: é tudo que o editor usa, e o painel do consultor
 * (`/sales/management/consultant`) não tem o cadastro inteiro — tem o código
 * do ERP e o vínculo que casou com ele.
 */
type AwardOwner = Pick<Consultant, 'id'>;

/** Quantas premiações cabem numa página da lista — ver o bloco da paginação. */
const AWARDS_PER_PAGE = 6;

export function ConsultantAwards({
  consultant,
  frame = 'modal',
}: {
  consultant: AwardOwner;
  /**
   * Onde a seção é desenhada. `modal` é a caixa de 672px da ficha do
   * consultor e da lista do Painel: uma coluna, que rola por dentro. `painel`
   * é o `SidePanel` largo do painel do consultor (23/10/2026): a lista cresce
   * com a página e vira duas colunas quando o painel tem largura — medida pelo
   * CONTAINER, nunca pela janela, pelo motivo escrito na pilha abaixo.
   */
  frame?: 'modal' | 'painel';
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  /*
   * Duas ações abrem o editor, e as rotas de `/sales/awards` aceitam qualquer
   * uma (`@PermissionAnyOf`): a da régua da base e, desde 23/10/2026, a do
   * Painel de Gestão — ver `sales.management:editar_premiacoes` no catálogo.
   */
  const canEdit =
    can(context, 'sales.commissions', 'configurar_premiacao') ||
    can(context, 'sales.management', 'editar_premiacoes');
  const client = useQueryClient();

  const awards = useQuery({
    queryKey: ['sales', 'awards', tenantId, consultant.id],
    queryFn: async () => {
      const r = await api.sales.listAwards({ query: { consultant: consultant.id } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'as premiações'));
      return r.body;
    },
  });

  /*
   * Os tipos do espelho, para o cadastro da premiação direta marcar códigos em
   * vez de digitá-los. A rota é de outro recurso, que NÃO é a permissão desta
   * seção — por isso o erro é carregado junto e dito dentro do bloco: uma lista
   * vazia sem explicação afirmaria que o ERP não tem tipo nenhum, quando o que
   * houve foi uma porta fechada.
   */
  const filters = useQuery({
    queryKey: ['erp', 'vehicle-filters', tenantId],
    queryFn: async () => {
      const r = await api.erp.vehicleFilters();
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os tipos do cadastro do ERP'));
      return r.body;
    },
  });

  const [editing, setEditing] = useState<Award | 'novo' | null>(null);
  /*
   * Qual premiação está sendo copiada — e ela é EXCLUSIVA do formulário de
   * edição: os dois abrem no mesmo lugar da seção, e deixá-los conviver poria
   * duas caixas de cadastro numa coluna de 604px.
   */
  const [copying, setCopying] = useState<Award | null>(null);
  /** Quantas cópias a última chamada criou — o aviso que substitui a lista que não muda. */
  const [copied, setCopied] = useState<number | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  /*
   * ATIVAS e ARQUIVADAS separadas, e a lista paginada — 23/10/2026.
   *
   * Misturadas, a premiação arquivada aparecia entre as que pagam, com a mesma
   * régua e o mesmo título de uma ativa, e só um selo pequeno dizia que ela
   * não conta mais: a lista parecia ter o dobro do que paga, com repetidas. A
   * arquivada continua a um clique — desarquivar é um ato desta seção —, mas
   * não no caminho de quem confere o que a pessoa recebe.
   */
  const [view, setView] = useState<'ativas' | 'arquivadas'>('ativas');
  const [page, setPage] = useState(1);
  const all = awards.data ?? [];
  const active = all.filter((a) => a.archivedAt === null);
  const archived = all.filter((a) => a.archivedAt !== null);
  const shown = view === 'ativas' || archived.length === 0 ? active : archived;
  /*
   * Arquivar a última premiação de uma página a esvazia, e a página pedida
   * deixa de existir: a conta volta para a última que existe em vez de
   * desenhar uma página vazia sob uma lista que tem itens.
   */
  const lastPage = Math.max(1, Math.ceil(shown.length / AWARDS_PER_PAGE));
  const currentPage = Math.min(page, lastPage);
  const paged = shown.slice(
    (currentPage - 1) * AWARDS_PER_PAGE,
    currentPage * AWARDS_PER_PAGE,
  );
  const switchView = (next: 'ativas' | 'arquivadas') => {
    setView(next);
    setPage(1);
  };

  /*
   * A pilha tem altura máxima, então ela pode cortar — e uma região que corta
   * sem alcançar o teclado é falha da WCAG 2.1.1. Quem decide se a região
   * existe para o teclado é a medida do navegador, nunca a contagem de
   * premiações: três cartões cabem numa janela alta e transbordam numa baixa.
   */
  const list = useOverflow('vertical');

  const archive = useMutation({
    mutationFn: async (input: { id: number; archived: boolean }) => {
      const r = await api.sales.editAward({
        params: { id: input.id },
        body: { action: 'arquivar', archived: input.archived },
      });
      if (r.status !== 200) throw new Error(refusalOf(r, 'a premiação'));
      return r.body;
    },
    onSuccess: () => {
      setFailure(null);
      void client.invalidateQueries({ queryKey: ['sales', 'awards'] });
      void client.invalidateQueries({ queryKey: ['sales', 'management'] });
    },
    onError: (e: Error) => setFailure(e.message),
  });

  return (
    <Card
      title="Premiações desta pessoa"
      descricao="Clique numa premiação para ver os detalhes."
      actions={
        canEdit && editing === null && copying === null ? (
          <Button size="sm" onClick={() => setEditing('novo')}>
            Nova premiação
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <ExistingAwards
          owner={consultant}
          canEdit={canEdit}
          onCopied={() => {
            setFailure(null);
            void client.invalidateQueries({ queryKey: ['sales', 'awards'] });
            void client.invalidateQueries({ queryKey: ['sales', 'management'] });
          }}
          onFailure={setFailure}
        />

        {failure !== null && <Alert tone="erro">{failure}</Alert>}
        {awards.isError && <Alert tone="erro">{awards.error.message}</Alert>}

        {editing !== null && (
          <AwardForm
            award={editing === 'novo' ? null : editing}
            consultant={consultant}
            filters={filters.data ?? null}
            filtersFailure={filters.isError ? filters.error.message : null}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              setFailure(null);
              void client.invalidateQueries({ queryKey: ['sales', 'awards'] });
              void client.invalidateQueries({ queryKey: ['sales', 'management'] });
            }}
            onFailure={setFailure}
          />
        )}

        {copying !== null && (
          <CopyAwardPanel
            award={copying}
            owner={consultant}
            onClose={() => setCopying(null)}
            onCopied={(count) => {
              setCopying(null);
              setFailure(null);
              /*
               * As cópias nascem na ficha de OUTRAS pessoas, e esta tela mostra
               * só as desta — a lista daqui não muda. Sem a invalidação larga,
               * abrir o consultor copiado em seguida mostraria a régua antiga
               * em cache, e quem copiou concluiria que não funcionou.
               */
              void client.invalidateQueries({ queryKey: ['sales', 'awards'] });
              void client.invalidateQueries({ queryKey: ['sales', 'management'] });
              setCopied(count);
            }}
            onFailure={setFailure}
          />
        )}

        {/*
         * O resultado de copiar não aparece nesta tela — as cópias são de
         * outras pessoas. Sem esta frase, o painel fecha e nada muda à vista, e
         * a leitura natural é que o clique não fez nada.
         */}
        {copied !== null && (
          <Alert tone="sucesso">
            {copied === 1
              ? 'Premiação copiada para 1 consultor. Ela aparece na ficha dele, e editá-la não toca nesta.'
              : `Premiação copiada para ${copied} consultores. Ela aparece na ficha de cada um, e editar uma não toca nas outras.`}
          </Alert>
        )}

        {awards.isLoading && <span className="esqueleto block h-20 w-full" />}

        {archived.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-2"
            role="group"
            aria-label="Quais premiações mostrar"
          >
            <Button
              size="sm"
              variant={shown === active ? 'primario' : 'contorno'}
              aria-pressed={shown === active}
              onClick={() => switchView('ativas')}
            >
              {`Ativas (${active.length})`}
            </Button>
            <Button
              size="sm"
              variant={shown === archived ? 'primario' : 'contorno'}
              aria-pressed={shown === archived}
              onClick={() => switchView('arquivadas')}
            >
              {`Arquivadas (${archived.length})`}
            </Button>
          </div>
        )}

        {awards.data !== undefined && all.length === 0 ? (
          <NotProvided>
            Esta pessoa não tem premiação cadastrada. Enquanto não tiver, o painel de
            gestão dela mostra a carteira e nenhum valor — que é o estado honesto, e não
            um defeito.
          </NotProvided>
        ) : awards.data !== undefined && shown.length === 0 ? (
          <NotProvided>
            Nenhuma premiação ativa — todas as desta pessoa estão arquivadas, e nenhuma
            paga. Desarquive uma em “Arquivadas” ou cadastre uma nova.
          </NotProvided>
        ) : frame === 'painel' ? (
          /*
           * No painel largo, a lista não rola por dentro: o corpo do
           * `SidePanel` já rola, e uma segunda barra dentro dele esconderia
           * cartões atrás de duas rolagens. As duas colunas vêm do CONTAINER
           * (`@container` + `@3xl:`), que é a largura que a lista tem de fato.
           */
          <div className="@container">
            <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-2">
              {paged.map((award) => (
                <AwardCard
                  key={award.id}
                  award={award}
                  canEdit={canEdit}
                  onEdit={() => setEditing(award)}
                  onCopy={() => {
                    setEditing(null);
                    setCopied(null);
                    setCopying(award);
                  }}
                  onArchive={() =>
                    archive.mutate({ id: award.id, archived: award.archivedAt === null })
                  }
                />
              ))}
            </div>
          </div>
        ) : (
          /*
           * Uma PILHA, e não uma grade — 22/09/2026.
           *
           * Era `grid-cols-1 xl:grid-cols-2`, e o `xl:` mede a JANELA, nunca o
           * container. Os dois lugares que desenham esta seção são modais
           * `size="lg"` — 672px —, então num monitor de 1280 o `xl:` disparava
           * dentro de uma caixa que nunca teve largura para duas trilhas: os
           * 672px viravam duas de ~320px. Com UMA premiação, que é o caso
           * comum, o cartão ficava com metade da largura e a outra metade
           * vazia; com duas, `tipos de boleto: 5, 51, 58, 75` quebrava em três
           * linhas. Medido em 22/09: a lista tem 612px e o cartão, 604.
           *
           * `CardGrid` não serve aqui: ele acerta o caso de um cartão
           * (`cards > 1`) e erra o mesmo `xl:`, porque também mede a janela. O
           * docblock dele já aponta a saída — "quem quer a vertical não usa
           * esta grade".
           */
          <div
            ref={list.ref}
            className={cn(
              /*
               * 28rem são três cartões inteiros e o começo do quarto, e é o
               * quarto cortado ao meio que diz que há mais — uma caixa que
               * termina exatamente no fim de um cartão parece o fim da lista.
               * `60vh` é quem manda na janela baixa: o modal inteiro já cabe em
               * `100dvh-2rem`, e a pilha não pode comer o cabeçalho, o botão
               * "Nova premiação" e o aviso das sobreposições — que é o motivo
               * de ela rolar por dentro em vez de deixar o corpo do modal rolar.
               */
              'flex max-h-[min(60vh,28rem)] flex-col gap-4 overflow-y-auto',
              /*
               * Os 4px de folga existem porque a sombra do cartão e o anel de
               * foco dos botões desenham FORA da borda: `overflow-y: auto` faz
               * o eixo horizontal virar `auto` junto (é regra do CSS, não
               * escolha), e sem a folga os dois apareceriam cortados na lateral
               * com uma barra de rolagem que ninguém pediu. O `-mx-1` devolve o
               * alinhamento com o resto do cartão.
               */
              '-mx-1 px-1',
              list.overflows && 'focus-visible:outline-offset-[-2px]',
            )}
            /* sem transbordo não há o que rolar, e a região deixa de existir
               para o teclado — mesma regra da `Table` */
            {...(list.overflows
              ? {
                  role: 'region' as const,
                  'aria-label': 'Premiações desta pessoa',
                  tabIndex: 0,
                }
              : {})}
          >
            {paged.map((award) => (
              <AwardCard
                key={award.id}
                award={award}
                canEdit={canEdit}
                onEdit={() => setEditing(award)}
                onCopy={() => {
                  setEditing(null);
                  setCopied(null);
                  setCopying(award);
                }}
                onArchive={() =>
                  archive.mutate({ id: award.id, archived: award.archivedAt === null })
                }
              />
            ))}
          </div>
        )}

        {/*
         * O que o cadastro NÃO recusa, dito onde a decisão é tomada.
         *
         * As duas travas de sobreposição saíram em 16/09, e nada as substituiu.
         * Sem esta frase, quem cadastra a segunda premiação do mesmo período
         * supõe que o sistema teria reclamado se houvesse problema — e a
         * diferença aparece no contracheque.
         */}
        {shown.length > AWARDS_PER_PAGE && (
          <Pagination
            page={currentPage}
            perPage={AWARDS_PER_PAGE}
            total={shown.length}
            onChange={setPage}
            label="premiações"
          />
        )}

        {active.length > 1 && (
          <p className="text-xs text-texto-suave">
            Premiações sobre o mesmo contrato <strong>pagam as duas</strong>.
          </p>
        )}
      </div>
    </Card>
  );
}

/**
 * O período de contratos como uma frase — com o fim que pode não existir.
 *
 * O começo é sempre preenchido e o fim é opcional (decisão de 16/09/2026:
 * começar sempre se sabe, terminar nem sempre). "em diante" precisa ser uma
 * palavra, e não uma ponta vazia: um `+` cru escreveria `contratos de
 * 2026-02-01 a null`, que é a tela afirmando o que o cadastro não diz.
 */
function periodLabel(award: { contractFrom: string; contractUntil: string | null }) {
  return award.contractUntil === null
    ? `contratos de ${award.contractFrom} em diante`
    : `contratos de ${award.contractFrom} a ${award.contractUntil}`;
}

/**
 * O prazo de ativação como uma frase — e a ausência dele DITA.
 *
 * "sem prazo" não é campo em branco: é a caixa "Considerar regra de 45 dias?"
 * desmarcada, e muda quem entra na contagem. Esconder a linha quando o prazo é
 * nulo faria as duas premiações parecerem iguais no cartão, e a diferença entre
 * elas é quantas placas contam.
 */
function deadlineLabel(award: { deadlineDays: number | null }) {
  return award.deadlineDays === null
    ? 'sem prazo para o 1º boleto'
    : `1º boleto em até ${award.deadlineDays} dias`;
}

/**
 * A régua de uma premiação, sem dono — o que faz duas premiações serem "a
 * mesma" na lista das já criadas. Tudo que muda quanto se paga entra; o id, o
 * dono, o modelo e o arquivamento não.
 */
function ruleKey(a: Award): string {
  return JSON.stringify([
    a.title,
    a.family,
    a.category,
    a.basisSource,
    a.percentage,
    a.deadlineDays,
    a.fromSecondBoleto,
    a.tiers,
    a.vehicleKinds,
    a.adhesionKinds,
    a.products,
    a.productCombination,
    a.productDiscount,
    a.contractFrom,
    a.contractUntil,
    a.boletoTypes.map((t) => t.code),
  ]);
}

/** Minúsculo e sem acento: "recorrência" casa com "recorrencia" digitado no celular. */
function folded(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * O texto em que a busca das premiações já criadas procura: título, família,
 * categoria, valor, quem tem, tipos de boleto e produtos — tudo que a linha
 * mostra ou que alguém lembraria ao procurar uma régua.
 */
function searchableText(award: Award, owners: string[]): string {
  return folded(
    [
      award.title,
      familyLabel(award.family),
      award.category,
      award.basisSource === 'hierarquia' ? 'de líder' : '',
      ruleValue(award),
      ...owners,
      ...award.boletoTypes.map((t) => `${t.code} ${t.descricao ?? ''}`),
      ...award.products.map((p) => `${p.code} ${p.label ?? ''}`),
    ].join(' '),
  );
}

/** O valor da régua numa frase curta — o mesmo que o cartão diz em destaque. */
function ruleValue(a: Award): string {
  if (a.family === 'recorrencia')
    return a.percentage === null
      ? 'sem percentual'
      : `${formatPercentage(a.percentage)}%`;
  if (a.family === 'produto') return `${a.products.length} produto(s)`;
  return `${a.tiers.length} faixa(s)`;
}

/**
 * "Premiações já criadas" — as réguas que JÁ EXISTEM na base, nas fichas de
 * outros consultores (23/10/2026, a pedido do dono).
 *
 * Fechada por padrão, logo abaixo do título da seção: é consulta, e não o que
 * a pessoa recebe — aberta, ela empurraria as premiações desta pessoa para
 * baixo de uma lista que é de outras. Só busca ao abrir.
 *
 * As iguais viram UMA linha, com quem as tem: a mesma régua copiada para
 * trinta consultores são trinta premiações no banco e uma decisão na tela.
 *
 * "Usar nesta pessoa" é a cópia de `POST /sales/awards/:id/copy` — a mesma
 * ação de "Copiar para…", no sentido contrário. A cópia nasce independente:
 * editar uma depois não toca na outra.
 */
function ExistingAwards({
  owner,
  canEdit,
  onCopied,
  onFailure,
}: {
  owner: AwardOwner;
  canEdit: boolean;
  onCopied: () => void;
  onFailure: (message: string) => void;
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const [open, setOpen] = useState(false);
  const [used, setUsed] = useState<string | null>(null);
  const [term, setTerm] = useState('');

  const all = useQuery({
    queryKey: ['sales', 'awards', tenantId, 'todas'],
    enabled: open,
    queryFn: async () => {
      const r = await api.sales.listAwards({ query: {} });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'as premiações da base'));
      return r.body;
    },
  });

  /* Ativas, de OUTRAS pessoas, agrupadas pela régua. */
  const groups = new Map<string, { award: Award; owners: string[] }>();
  for (const a of all.data ?? []) {
    if (a.archivedAt !== null || a.consultantId === owner.id) continue;
    const key = ruleKey(a);
    const group = groups.get(key);
    if (group) group.owners.push(a.consultant.user.nome);
    else groups.set(key, { award: a, owners: [a.consultant.user.nome] });
  }
  const rules = [...groups.values()].sort((x, y) =>
    x.award.title.localeCompare(y.award.title, 'pt-BR'),
  );
  /*
   * Cada palavra digitada precisa casar em algum lugar da linha, em qualquer
   * ordem: "5% direta" acha a régua de 5% da família direta sem exigir que as
   * duas palavras estejam juntas no título.
   */
  const words = folded(term).split(/\s+/).filter(Boolean);
  const found =
    words.length === 0
      ? rules
      : rules.filter(({ award, owners }) => {
          const text = searchableText(award, owners);
          return words.every((w) => text.includes(w));
        });

  const copy = useMutation({
    mutationFn: async (award: Award) => {
      const r = await api.sales.copyAward({
        params: { id: award.id },
        body: { consultantIds: [owner.id] },
      });
      if (r.status !== 201) throw new Error(refusalOf(r, 'a premiação'));
      return award.title;
    },
    onSuccess: (title) => {
      setUsed(title);
      onCopied();
    },
    onError: (e: Error) => onFailure(e.message),
  });

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title="Ver as premiações que já existem nesta base, nas fichas de outros consultores"
        className={linkTextClasses('gap-2 self-start text-sm font-semibold')}
      >
        <Icon nome={open ? 'seta-baixo' : 'seta-direita'} size={16} />
        Premiações já criadas
      </button>

      {open && (
        <div
          className={cardClasses({
            surface: 'suave',
            className:
              'flex flex-col gap-3 animate-in fade-in-0 slide-in-from-top-1 duration-200 motion-reduce:animate-none',
          })}
        >
          <span className="text-xs text-texto-suave">
            As réguas ativas desta base, nas fichas de outros consultores. Iguais aparecem
            uma vez, com quem as tem.
            {canEdit && ' “Usar nesta pessoa” cria uma cópia própria dela.'}
          </span>

          {used !== null && (
            <Alert tone="sucesso">
              “{used}” foi copiada para esta pessoa e já aparece na lista abaixo.
            </Alert>
          )}
          {all.isError && <Alert tone="erro">{all.error.message}</Alert>}
          {all.isLoading && <span className="esqueleto block h-16 w-full" />}
          {all.data !== undefined && rules.length === 0 && (
            <NotProvided>
              Nenhuma outra pessoa desta base tem premiação ativa.
            </NotProvided>
          )}

          {rules.length > 0 && (
            <div className="flex flex-col gap-1">
              <Field
                label="Pesquisar premiações já criadas"
                labelHidden
                icon="busca"
                type="search"
                placeholder="Título, tipo, valor, consultor, produto ou tipo de boleto"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
              />
              {/* lida por leitor de tela a cada tecla — o resultado da busca */}
              <span className="text-xs text-texto-suave" aria-live="polite">
                {words.length === 0
                  ? `${rules.length} régua(s)`
                  : `${found.length} de ${rules.length} régua(s)`}
              </span>
            </div>
          )}

          {rules.length > 0 && found.length === 0 && (
            <NotProvided>
              Nenhuma régua casa com “{term.trim()}”. A busca olha título, tipo, valor,
              consultor, produto e tipo de boleto.
            </NotProvided>
          )}

          {found.length > 0 && (
            /*
             * `pr-3` e a canaleta reservada (`scrollbar-gutter: stable`): sem os
             * dois, a barra de rolagem desenhava por cima do "Usar nesta
             * pessoa", colado à borda direita da lista.
             */
            <ul className="flex max-h-80 flex-col divide-y divide-borda overflow-y-auto pr-3 [scrollbar-gutter:stable]">
              {found.map(({ award, owners }) => (
                <li
                  key={award.id}
                  className="flex flex-wrap items-center justify-between gap-3 py-3"
                >
                  <div className="flex min-w-0 flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone="neutro">{award.category}</Badge>
                      <Badge tone="neutro">{familyLabel(award.family)}</Badge>
                      {award.basisSource === 'hierarquia' && (
                        <Badge tone="informacao">de líder</Badge>
                      )}
                      <span className="font-semibold text-texto">{award.title}</span>
                      <span className="text-sm font-bold text-texto">
                        {ruleValue(award)}
                      </span>
                    </div>
                    <span className="text-xs text-texto-suave">
                      {periodLabel(award)} ·{' '}
                      {owners.length === 1
                        ? owners[0]
                        : `${owners.length} consultores: ${owners.slice(0, 3).join(', ')}${owners.length > 3 ? '…' : ''}`}
                    </span>
                  </div>
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="contorno"
                      className="shrink-0"
                      disabled={copy.isPending}
                      onClick={() => copy.mutate(award)}
                    >
                      Usar nesta pessoa
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Os tipos de boleto, sempre do mesmo jeito: uma linha por tipo, o CÓDIGO
 * primeiro e o nome ao lado.
 *
 * Era `descricao ?? code` numa frase só, e a lista saía metade em nome e
 * metade em número — o nome só existe quando o espelho conhece o tipo.
 */
function BoletoTypeList({
  types,
}: {
  types: readonly { code: string; descricao: string | null }[];
}) {
  if (types.length === 0) return <>—</>;
  return (
    <ul className="flex flex-col gap-0.5">
      {types.map((t) => (
        <li key={t.code} className="flex gap-2">
          <span className="w-10 shrink-0 font-semibold tabular-nums">{t.code}</span>
          <span className={t.descricao === null ? 'text-texto-suave' : undefined}>
            {t.descricao ?? 'sem nome no ERP'}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Por que a premiação não apura, quando não apura — a lista obrigatória que
 * ficou vazia. `null` é "apura".
 */
function missingOf(award: Award): string | null {
  if (countsActivations(award.family)) {
    if (award.tiers.length === 0) return 'sem faixa';
    if (award.vehicleKinds.length === 0) return 'sem tipo de veículo';
    if (award.adhesionKinds.length === 0) return 'sem tipo de adesão';
    if (award.boletoTypes.length === 0)
      return 'sem tipo de boleto de primeira mensalidade';
    return null;
  }
  if (award.family === 'produto' && award.products.length === 0) return 'sem produto';
  if (award.boletoTypes.length === 0) return 'sem tipo de boleto';
  return null;
}

/**
 * O cartão de uma premiação (30/10/2026): NOME, LOTE (1x/2x) e VALOR — e só.
 *
 * O resto — família, regra do 1º boleto, tipos de boleto, produtos, faixas e
 * os botões — abre ao clicar no cartão. A exceção é o selo "não apura": a
 * premiação que existe e não paga nada é o cadastro mais silencioso desta
 * tela, e escondê-lo atrás de um clique o deixaria passar.
 */
function AwardCard({
  award,
  canEdit,
  onEdit,
  onCopy,
  onArchive,
}: {
  award: Award;
  canEdit: boolean;
  onEdit: () => void;
  onCopy: () => void;
  onArchive: () => void;
}) {
  const [open, setOpen] = useState(false);
  const archived = award.archivedAt !== null;
  const missing = missingOf(award);

  return (
    <div className={cardClasses({ surface: 'suave', className: 'flex flex-col p-0' })}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 rounded-[inherit] px-4 py-3 text-left transition-colors hover:bg-superficie"
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="truncate font-semibold text-texto">{award.title}</span>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone="neutro">{award.category}</Badge>
            {missing !== null && <Badge tone="alerta">não apura</Badge>}
            {archived && <Badge tone="alerta">arquivada</Badge>}
          </div>
        </div>
        <span className="shrink-0 text-lg font-extrabold whitespace-nowrap text-texto tabular-nums">
          <AwardValue award={award} />
        </span>
        <Icon
          nome={open ? 'seta-cima' : 'seta-baixo'}
          size={16}
          className="shrink-0 text-texto-suave"
        />
      </button>

      {open && (
        <div className="flex flex-col gap-3 border-t border-borda px-4 py-3 text-sm">
          {missing !== null && (
            <Alert tone="alerta">{`${capitalized(missing)} — esta premiação não apura.`}</Alert>
          )}

          <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[9rem_minmax(0,1fr)]">
            <dt className="text-texto-suave">Família</dt>
            <dd>
              {familyLabel(award.family)}
              {/* a de líder paga sobre os boletos do time, e não os do dono */}
              {award.basisSource === 'hierarquia' && ' · de líder'}
            </dd>

            <dt className="text-texto-suave">Regra</dt>
            <dd>
              {countsActivations(award.family)
                ? `${award.tiers.length} faixa(s), de ${award.tiers[0]?.minPlates ?? 0} a ${award.tiers[award.tiers.length - 1]?.minPlates ?? 0} placas · ${deadlineLabel(award)}`
                : award.family === 'produto'
                  ? (award.productCombination === 'maior'
                      ? 'paga o maior produto'
                      : 'soma os produtos') +
                    (award.productDiscount === null
                      ? ''
                      : ` · −${formatPercentage(award.productDiscount.percentage)}% de ${formatDay(award.productDiscount.from)} em diante`)
                  : award.fromSecondBoleto
                    ? 'a partir do 2º boleto'
                    : 'desde o 1º boleto'}
            </dd>

            <dt className="text-texto-suave">Vigência</dt>
            <dd>{periodLabel(award)}</dd>

            {countsActivations(award.family) && (
              <>
                <dt className="text-texto-suave">Veículos</dt>
                <dd>{codesOrNone(award.vehicleKinds)}</dd>
                <dt className="text-texto-suave">Adesão</dt>
                <dd>{codesOrNone(award.adhesionKinds)}</dd>
              </>
            )}

            <dt className="text-texto-suave">
              {countsActivations(award.family) ? 'Ativa com' : 'Tipos de boleto'}
            </dt>
            <dd>
              <BoletoTypeList types={award.boletoTypes} />
            </dd>

            {award.family === 'produto' && award.products.length > 0 && (
              <>
                <dt className="text-texto-suave">Produtos</dt>
                <dd className="flex flex-col gap-0.5">
                  {award.products.map((p) => (
                    <span key={p.code}>
                      {p.label ?? `código ${p.code}`} · <MoneyValue value={p.amount} />
                    </span>
                  ))}
                </dd>
              </>
            )}
          </dl>

          {canEdit && (
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="contorno" size="sm" onClick={onEdit}>
                Editar
              </Button>
              {/*
               * Copiar existe porque a régua da DIRETA tem 11 faixas por tabela,
               * e a premiação é de uma pessoa só desde 16/09.
               */}
              <Button variant="contorno" size="sm" onClick={onCopy}>
                Copiar para…
              </Button>
              <Button variant="contorno" size="sm" onClick={onArchive}>
                {archived ? 'Desarquivar' : 'Arquivar'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * O VALOR da premiação, como cada família o diz: a recorrência tem um
 * percentual; a direta e o produto têm uma tabela, e o que resume uma tabela é
 * o intervalo dela.
 */
function AwardValue({ award }: { award: Award }) {
  if (award.family === 'recorrencia') {
    return (
      <>{award.percentage === null ? '—' : formatPercentage(award.percentage) + '%'}</>
    );
  }
  const amounts =
    award.family === 'produto'
      ? award.products.map((p) => p.amount)
      : award.tiers.map((t) => t.amount);
  if (amounts.length === 0) return <>—</>;
  const low = minOf(amounts);
  const high = maxOf(amounts);
  return (
    <>
      <MoneyValue value={low} />
      {high !== low && (
        <>
          {' a '}
          <MoneyValue value={high} />
        </>
      )}
    </>
  );
}

function codesOrNone(codes: readonly string[]): string {
  return codes.length === 0 ? '—' : `códigos ${codes.join(', ')}`;
}

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Copiar uma premiação para outros consultores.
 *
 * ## O que ela resolve, e por que não é uma régua compartilhada
 *
 * A premiação pertence a uma pessoa desde 16/09/2026, e as duas tabelas que o
 * responsável entregou têm **11 faixas cada**: dez consultores com as duas são
 * 220 linhas digitadas à mão. O erro de digitação não dá erro nenhum — dá uma
 * faixa diferente num consultor só, descoberta pelo contracheque.
 *
 * O que sai daqui são premiações **independentes**, e a tela diz isso duas
 * vezes: antes de copiar, para quem espera que editar uma mude todas, e depois,
 * porque as cópias nascem na ficha de OUTRAS pessoas e a lista desta tela não
 * muda.
 *
 * ## O dono não aparece na lista
 *
 * Copiar para ele criaria uma segunda régua igual na mesma pessoa, e as duas
 * pagariam sobre os mesmos boletos — não há trava de sobreposição no banco
 * desde 16/09. O serviço recusa com uma frase; a tela nem oferece, que é onde o
 * engano nasceria.
 */
function CopyAwardPanel({
  award,
  owner,
  onClose,
  onCopied,
  onFailure,
}: {
  award: Award;
  owner: AwardOwner;
  onClose: () => void;
  onCopied: (count: number) => void;
  onFailure: (message: string) => void;
}) {
  const consultants = useConsultants();
  const [selected, setSelected] = useState<number[]>([]);

  /*
   * Fora o dono. `useConsultants` já traz só os ativos e não arquivados, que é
   * a mesma régua de `requireConsultant` no servidor — oferecer um arquivado
   * daria 404 numa linha que a tela desenhou.
   */
  const options = (consultants.data ?? []).filter((c) => c.id !== owner.id);

  const copy = useMutation({
    mutationFn: async () => {
      const r = await api.sales.copyAward({
        params: { id: award.id },
        body: { consultantIds: selected },
      });
      if (r.status !== 201) throw new Error(refusalOf(r, 'a premiação'));
      return r.body;
    },
    onSuccess: (copies) => onCopied(copies.length),
    onError: (e: Error) => onFailure(e.message),
  });

  const toggle = (id: number, on: boolean) =>
    setSelected((current) =>
      on ? [...new Set([...current, id])] : current.filter((c) => c !== id),
    );

  return (
    <div className={cardClasses({ surface: 'suave', className: 'flex flex-col gap-4' })}>
      <div className="flex flex-col gap-1">
        <span className="font-semibold text-texto">Copiar “{award.title}”</span>
        <span className="text-xs text-texto-suave">
          Cada pessoa marcada ganha uma premiação <strong>própria</strong>, com esta régua
          inteira — período, categoria
          {countsActivations(award.family)
            ? `, prazo e as ${award.tiers.length} faixa(s)`
            : award.family === 'produto'
              ? `, os ${award.products.length} produto(s) e os tipos de boleto`
              : ', percentual e os tipos de boleto'}
          . Depois de copiada, editar a de uma não toca na das outras.
        </span>
      </div>

      {consultants.isError && <Alert tone="erro">{consultants.error.message}</Alert>}
      {consultants.isLoading && <span className="esqueleto block h-16 w-full" />}

      {consultants.data !== undefined && options.length === 0 ? (
        <NotProvided>
          Esta base não tem outro consultor ativo para receber a cópia. Vincule mais
          alguém em “Consultores” antes de copiar.
        </NotProvided>
      ) : (
        <fieldset className="flex flex-col">
          <legend className="rotulo mb-2">Copiar para</legend>
          <div className="flex max-h-56 flex-col gap-2 overflow-y-auto">
            {options.map((c) => (
              <label key={c.id} className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selected.includes(c.id)}
                  onChange={(e) => toggle(c.id, e.target.checked)}
                />
                <span>
                  {c.user.nome}
                  <span className="block text-xs text-texto-suave">
                    {'código no ERP: ' + c.erpVolunteerCode}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          /*
           * Zero marcado desabilita, e o contrato exige ao menos um: sem isso o
           * clique viraria 400 do Zod, cuja frase é do esquema e não da tela.
           */
          disabled={selected.length === 0 || copy.isPending}
          onClick={() => copy.mutate()}
        >
          {copy.isPending
            ? 'Copiando…'
            : selected.length <= 1
              ? 'Copiar'
              : `Copiar para ${selected.length}`}
        </Button>
        <Button variant="contorno" size="sm" onClick={onClose} disabled={copy.isPending}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

/**
 * O que está preenchido errado, campo a campo.
 *
 * Devolve um mapa de campo → frase; vazio é "dá para salvar". Cada frase diz **o
 * que fazer**, e não o que aconteceu: "use só números" resolve, "valor inválido"
 * manda adivinhar.
 *
 * ## Por que o vazio das datas tem frase própria
 *
 * Nem o controle nativo nem o `DateField` deixam uma data impossível chegar
 * aqui: 31/02 vira **string vazia** nos dois. O problema relatado nunca foi o
 * 31/02 passar — era o campo esvaziar sem dizer nada, e o erro só aparecer ao
 * salvar, em cima.
 *
 * O `DateField` fechou metade disso em 21/09/2026: ele DIZ, no próprio campo,
 * que aquele dia não existe. Esta frase continua porque cobre o outro caminho —
 * o campo deixado em branco, que não é erro de digitação e não tem o que o
 * campo acuse.
 *
 * Ele entrou por outro motivo, e maior: `<input type=date>` não lê o `lang` da
 * página e mostrava `mm/dd/aaaa` em navegador em inglês. Foi aqui que isso
 * custou caro — `01/02/2026` gravou 2 de janeiro numa janela de vigência de
 * régua de pagamento. Ver o cabeçalho de `packages/ui/src/DateField.tsx`.
 */
function validate(f: {
  family: AwardFamily;
  percentage: string;
  useDeadline: boolean;
  deadline: string;
  tiers: TierDraft[];
  products: ProductChoice[];
  useDiscount: boolean;
  discountFrom: string;
  discountPercentage: string;
  from: string;
  until: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};

  if (f.family === 'produto') {
    /*
     * O produto MARCADO sem valor gravaria um produto que paga R$ 0,00 — e o
     * banco recusa (`sales_award_products_amount_ck`). A frase vem antes, no
     * campo. A régua vazia não é cobrada: quem recusa é a apuração.
     */
    if (f.products.some((p) => !(Number(p.amount.replace(',', '.')) > 0))) {
      errors.products =
        'Há produto marcado sem valor. Informe quanto ele paga, ou desmarque.';
    }
    if (f.useDiscount) {
      if (f.discountFrom === '') {
        errors.discountFrom =
          'Informe a partir de qual data de contrato o desconto reduz.';
      }
      const n = Number(f.discountPercentage.replace(',', '.'));
      if (!(n > 0 && n <= 100)) {
        errors.discountPercentage = 'O percentual tirado vai de 0 a 100 — 20 para 20%.';
      }
    }
  }

  if (countsActivations(f.family)) {
    /*
     * A régua não é obrigatória para salvar — uma premiação pode ser cadastrada
     * antes de a tabela ser digitada, e quem recusa é a apuração, com o nome do
     * que falta. O que se cobra aqui é a linha PELA METADE: um piso sem valor
     * gravaria uma faixa de R$ 0,00, que paga zero com cara de régua.
     */
    const incomplete = f.tiers.some(
      (t) => t.minPlates.trim() === '' || t.amount.trim() === '',
    );
    if (incomplete) {
      errors.tiers =
        'Há faixa sem quantidade ou sem valor. Preencha as duas, ou remova a linha.';
    }

    const plates = f.tiers.map((t) => t.minPlates.trim()).filter((p) => p !== '');
    if (new Set(plates).size !== plates.length) {
      errors.tiers = 'Há duas faixas com a mesma quantidade de placas.';
    }

    /*
     * O prazo só é cobrado com a caixa MARCADA. Desmarcada, o campo nem
     * aparece, e cobrar um valor de um campo que não está na tela é como o erro
     * fica sem lugar para ser consertado.
     */
    if (f.useDeadline && (f.deadline.trim() === '' || Number(f.deadline) <= 0)) {
      errors.deadline = 'Informe o prazo em dias — 45 é o das tabelas de hoje.';
    }
  }

  const percentage = f.percentage.trim();
  if (f.family !== 'recorrencia') {
    /* Só a recorrência tem percentual: nas outras o campo nem aparece. */
  } else if (percentage === '') {
    errors.percentage = 'Informe o percentual.';
  } else {
    const n = Number(percentage.replace(',', '.'));
    if (!Number.isFinite(n)) {
      errors.percentage = 'Use só números e vírgula — 7,5 para 7,5%. O “%” não entra.';
    } else if (n <= 0) {
      errors.percentage = 'O percentual precisa ser maior que zero.';
    } else if (n > 100) {
      errors.percentage = 'O percentual não passa de 100.';
    }
  }

  /*
   * O COMEÇO é obrigatório e o FIM é opcional, e a assimetria é a decisão:
   * começar sempre se sabe, terminar nem sempre (16/09/2026).
   *
   * "Contratos até" em branco é **sem limite** — a premiação alcança o contrato
   * que ainda vai ser feito —, e não um campo esquecido. Até 15/09 as duas
   * pontas eram obrigatórias, e o cadastro tinha de inventar uma data distante
   * o bastante para "daqui em diante".
   *
   * A `category` não decide nada sobre estas duas. Ela decidiu por algumas
   * horas no mesmo dia (`1x` cobrava o fim, `2x` o começo), e o responsável
   * substituiu a régua: ela voltou a ser só o lote de pagamento.
   */
  if (f.from === '') {
    errors.from = 'Informe a partir de quando os contratos contam, ou a data não existe.';
  }

  /*
   * A ordem das duas, e ela é a mesma que o `check` do banco cobra
   * (`sales_awards_range_ck`). Aqui a frase diz qual das duas mexer.
   */
  if (f.from !== '' && f.until !== '' && f.until < f.from) {
    errors.until = 'A data final é anterior à inicial.';
  }

  return errors;
}

/**
 * O formulário — o mesmo para criar e para editar, premiação ou MODELO.
 *
 * **O dono não é um campo.** Ele vem de quem abriu a tela, e o formulário só o
 * repassa: uma premiação criada aqui é desta pessoa, e editá-la aqui não a move
 * para outra. Um seletor de consultor faria a tela do consultor A poder gravar
 * a premiação do B — que é exatamente a tela antiga, desenhada dentro da nova.
 *
 * **Sem consultor (`null`), ele grava um MODELO** — a mesma régua, sem dono,
 * pela rota dos modelos (23/09/2026). É o mesmo formulário de propósito: um
 * segundo para os modelos divergiria deste no primeiro campo novo, e o modelo
 * passaria a criar premiações que o formulário da premiação não sabe editar.
 */
export function AwardForm({
  award,
  consultant,
  filters,
  filtersFailure,
  onClose,
  onSaved,
  onFailure,
  frame = 'card',
}: {
  award: Award | AwardTemplate | null;
  consultant: AwardOwner | null;
  filters: CatalogVehicleFilters | null;
  filtersFailure: string | null;
  onClose: () => void;
  /** `applied`: em quantas premiações vinculadas a edição do modelo foi aplicada. */
  onSaved: (applied: number) => void;
  /** Só no `card`: no `modal` a recusa aparece dentro dele, acima dos botões. */
  onFailure: (message: string) => void;
  /**
   * Onde o formulário se desenha.
   *
   * - `card`: no lugar, dentro da seção — é o da ficha do consultor, que JÁ
   *   abre num modal; um segundo modal por cima seria véu sobre véu.
   * - `modal`: no `Modal` da casa — a tela de modelos (15/10/2026). Lá o
   *   formulário abria no topo, acima da lista, e quem clicava "Editar" num
   *   modelo do fim da página não via nada acontecer: tinha de rolar até ele.
   *   É o mesmo caso que abriu a exceção do cadastro do comercial no
   *   `DESIGN.md` (18/09).
   */
  frame?: 'card' | 'modal';
}) {
  /*
   * A recusa do servidor, quando o formulário está num modal: a faixa da tela
   * fica atrás do véu, e a pessoa veria o botão destravar sem saber por quê.
   */
  const [refusal, setRefusal] = useState<string | null>(null);
  /*
   * Quem está vinculado ao MODELO em edição — e a quem a régua nova vai junto.
   * Nenhum marcado por padrão (15/10/2026): a premiação de alguém pode ter sido
   * ajustada só para ela, e sobrescrever é escolha de quem salva, não efeito
   * colateral de editar o modelo.
   */
  const linked =
    consultant === null && award !== null && 'consultants' in award
      ? award.consultants
      : [];
  const [applyTo, setApplyTo] = useState<number[]>([]);
  const [title, setTitle] = useState(award?.title ?? '');
  /*
   * O campo abre com o percentual JÁ formatado — `7,5`, e não `7.5000`.
   * Reapresentar as quatro casas da coluna faria quem só queria corrigir o
   * título salvar de volta um número que ele não digitou, e a vírgula é o
   * decimal que ele vai usar para digitar.
   */
  const [percentage, setPercentage] = useState(
    award === null || award.percentage === null ? '' : formatPercentage(award.percentage),
  );
  const [from, setFrom] = useState(award?.contractFrom ?? '');
  const [until, setUntil] = useState(award?.contractUntil ?? '');
  const [category, setCategory] = useState<'1x' | '2x'>(award?.category ?? '1x');
  /*
   * O calendário dos campos de data abre ACIMA do véu quando o formulário está
   * num modal — sem isto ele abria por baixo e não recebia clique, o mesmo
   * defeito do seletor do "Vincular" (15/10/2026).
   */
  const dateLayer = frame === 'modal' ? ('modal' as const) : undefined;
  /*
   * Os tipos de boleto MARCADOS na lista da base (14/10/2026) — eram um campo
   * de texto com códigos separados por vírgula, e o código digitado errado não
   * dava erro nenhum: dava uma premiação que não contava nada.
   */
  const [types, setTypes] = useState<BoletoTypeChoice[]>(award?.boletoTypes ?? []);
  const [family, setFamily] = useState<AwardFamily>(award?.family ?? 'recorrencia');
  /*
   * De QUEM são os boletos da base. O contrato aceitava `hierarquia` desde
   * 22/09 e este formulário nunca o mandava — a premiação de líder, que é a
   * única que se reparte, não tinha como nascer pela tela.
   */
  const [basisSource, setBasisSource] = useState<AwardBasisSource>(
    award?.basisSource ?? 'proprio',
  );
  /*
   * A CAIXA e o NÚMERO são dois estados, e não um só.
   *
   * O contrato transporta um campo — `deadlineDays`, nulo quando a regra não é
   * considerada —, e a tentação é derivar a caixa de `deadline !== ''`. Não dá:
   * quem desmarca e remarca perderia o 45 que já estava lá, e quem apaga o
   * campo para digitar outro número veria a caixa se desmarcar sozinha no meio
   * da digitação. Os dois se juntam de novo no `save`.
   *
   * Uma premiação nova abre com a caixa MARCADA e 45 no campo: é a regra das
   * duas tabelas que a casa usa hoje, e é o que quem cadastra espera encontrar.
   */
  const [useDeadline, setUseDeadline] = useState(
    award === null ? true : award.deadlineDays !== null,
  );
  /*
   * A regra do 2º boleto da recorrência. A premiação nova abre MARCADA: é o
   * comportamento de antes de 23/10/2026, e desmarcar é a decisão que muda o
   * valor — a Recorrência Direta 2% conta desde o 1º.
   */
  const [fromSecondBoleto, setFromSecondBoleto] = useState(
    award?.fromSecondBoleto ?? true,
  );
  const [deadline, setDeadline] = useState(
    award?.deadlineDays === null || award?.deadlineDays === undefined
      ? '45'
      : String(award.deadlineDays),
  );
  /*
   * As faixas viram TEXTO no estado, e voltam a número no salvar. O campo
   * devolve texto, e converter a cada tecla faria "2," virar 2 e o cursor
   * pular a vírgula que a pessoa acabou de digitar.
   */
  const [tiers, setTiers] = useState<TierDraft[]>(
    (award?.tiers ?? []).map((t) => ({
      minPlates: String(t.minPlates),
      amount: t.amount.replace('.', ','),
    })),
  );
  const [vehicleKinds, setVehicleKinds] = useState<string[]>(award?.vehicleKinds ?? []);
  const [adhesionKinds, setAdhesionKinds] = useState<string[]>(
    award?.adhesionKinds ?? [],
  );
  /*
   * A régua da família `produto` (14/10/2026): os produtos marcados, cada um
   * com o seu valor — texto no estado, pelo mesmo motivo das faixas.
   */
  const [products, setProducts] = useState<ProductChoice[]>(
    (award?.products ?? []).map((p) => ({
      code: p.code,
      label: p.label,
      amount: p.amount.replace('.', ','),
    })),
  );
  const [combination, setCombination] = useState<AwardProductCombination>(
    award?.productCombination ?? 'soma',
  );
  const [useDiscount, setUseDiscount] = useState(
    award !== null && award.productDiscount !== null,
  );
  const [discountFrom, setDiscountFrom] = useState(award?.productDiscount?.from ?? '');
  const [discountPercentage, setDiscountPercentage] = useState(
    award?.productDiscount === null || award?.productDiscount === undefined
      ? '20'
      : formatPercentage(award.productDiscount.percentage),
  );

  /*
   * Os erros de preenchimento, por campo — e a razão de eles existirem aqui.
   *
   * O banco e o contrato já recusam percentual fora de 0–100 e data invertida,
   * e é o banco que manda. Mas a recusa deles chega como **uma faixa vermelha
   * no topo da tela**, longe do campo que a causou: quem digitou `10%` via
   * "erro ao salvar" e não sabia onde estava o problema. Foi o defeito relatado.
   *
   * Então isto não duplica a regra — repete a mesma conclusão **onde ela se
   * conserta**. A validação de verdade continua no servidor; se as duas
   * divergirem, quem ganha é a de lá, e a tela mostra a frase que veio.
   */
  const errors = validate({
    family,
    percentage,
    useDeadline,
    deadline,
    tiers,
    products,
    useDiscount,
    discountFrom,
    discountPercentage,
    from,
    until,
  });
  const canSave = Object.keys(errors).length === 0;

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        title: title.trim(),
        family,
        /*
         * Cada família manda o SEU campo e nulo no da outra. O servidor
         * descarta o que sobra, mas mandar o percentual de uma direta faria a
         * requisição carregar um número que ninguém escolheu — e um dia alguém
         * o leria.
         */
        percentage:
          family === 'recorrencia' ? Number(percentage.replace(',', '.')) : null,
        /*
         * Os dois estados viram um campo: nulo é "não considera a regra do
         * prazo", e é o que a caixa desmarcada manda. O número digitado
         * continua no estado da tela, e volta se a caixa for remarcada antes de
         * salvar.
         */
        deadlineDays: countsActivations(family) && useDeadline ? Number(deadline) : null,
        /* Só a recorrência lê; nas outras o servidor grava verdadeiro. */
        fromSecondBoleto: family === 'recorrencia' ? fromSecondBoleto : true,
        tiers: countsActivations(family)
          ? tiers.map((t) => ({
              minPlates: Number(t.minPlates),
              /* O contrato quer ponto decimal; o campo aceita vírgula. */
              amount: t.amount.replace(',', '.'),
            }))
          : [],
        vehicleKinds: countsActivations(family) ? vehicleKinds : [],
        adhesionKinds: countsActivations(family) ? adhesionKinds : [],
        contractFrom: from,
        /*
         * O fim vazio vira NULO, e nulo é "sem limite". Mandar a string vazia
         * faria o Zod recusar o formato de data, e a recusa falaria de formato
         * em vez de régua. O começo não passa por aqui: a validação já barrou
         * o vazio, e `''` nunca chega ao corpo.
         */
        contractUntil: until === '' ? null : until,
        category,
        /* A direta não lê tipo de boleto; a recorrência e a `produto`, sim. */
        boletoTypes: types,
        products:
          family === 'produto'
            ? products.map((p) => ({
                code: p.code,
                label: p.label,
                amount: p.amount.replace(',', '.'),
              }))
            : [],
        productCombination: family === 'produto' ? combination : null,
        productDiscount:
          family === 'produto' && useDiscount
            ? {
                from: discountFrom,
                percentage: Number(discountPercentage.replace(',', '.')),
              }
            : null,
        /*
         * Só a recorrência tem base de líder: a apuração da direta conta as
         * placas do dono (`directEntries`), e mandar `hierarquia` numa direta
         * gravaria uma escolha que nenhum cálculo lê.
         */
        basisSource: family === 'recorrencia' ? basisSource : 'proprio',
      };

      /* O MODELO: a mesma régua, sem dono, pela rota dele. */
      if (consultant === null) {
        const r =
          award === null
            ? await api.sales.createAwardTemplate({ body })
            : await api.sales.editAwardTemplate({
                params: { id: award.id },
                body: { action: 'editar', ...body, applyTo },
              });
        if (r.status !== 200 && r.status !== 201)
          throw new Error(refusalOf(r, 'o modelo'));
        return r.body;
      }

      /*
       * O dono vem de quem abriu a tela, e não de um campo. Ver o docblock
       * deste formulário.
       */
      const owned = { ...body, consultantId: consultant.id };
      const r =
        award === null
          ? await api.sales.createAward({ body: owned })
          : await api.sales.editAward({
              params: { id: award.id },
              body: { action: 'editar', ...owned },
            });

      if (r.status !== 200 && r.status !== 201)
        throw new Error(refusalOf(r, 'a premiação'));
      return r.body;
    },
    onSuccess: () => onSaved(applyTo.length),
    onError: (e: Error) =>
      frame === 'modal' ? setRefusal(e.message) : onFailure(e.message),
  });

  const heading =
    consultant === null
      ? award === null
        ? 'Novo modelo'
        : `Editar modelo: ${award.title}`
      : award === null
        ? 'Nova premiação'
        : `Editar: ${award.title}`;

  const body = (
    <>
      {/*
       * O RITMO VERTICAL mora aqui, e não no `Card`.
       *
       * O corpo do `Card` é um `div` sem `gap` — ele posiciona a moldura, não o
       * conteúdo. Sem esta pilha, o bloco da régua e o dos botões encostam um
       * no outro, e foi o que a leitura de 15/09 apontou: o texto de apoio de
       * um bloco parecia rótulo do seguinte.
       *
       * 24px entre blocos, e é o mesmo número que separa uma linha de campos da
       * próxima — abaixo disso o apoio cola no que vem depois; acima, o
       * formulário deixa de ler como uma coisa só.
       */}
      <div className="flex flex-col gap-6">
        <fieldset className="flex flex-col">
          {/*
           * A LEGENDA nomeia o bloco, e ela nasceu de uma leitura de 15/09:
           * o formulário eram campos soltos e o primeiro terço não dizia o que
           * era, então a tela lia como uma pilha de controles em vez de uma
           * pergunta. Ela ficou depois de os outros dois blocos saírem, em
           * 16/09 — é ela que separa a régua dos botões.
           */}
          <legend className="rotulo mb-2">A régua: o que esta premiação paga</legend>

          {/*
           * A FAMÍLIA primeiro, porque ela decide quais campos existem abaixo.
           * Radio e não caixas: as duas são excludentes, e duas caixas
           * deixariam representáveis "as duas" e "nenhuma", que o banco não
           * tem como guardar.
           */}
          <div className="mb-4 flex flex-col gap-2">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="award-family"
                className="mt-1"
                checked={family === 'recorrencia'}
                onChange={() => setFamily('recorrencia')}
              />
              <span>
                Recorrência
                <span className="block text-xs text-texto-suave">
                  Percentual sobre os boletos pagos da carteira, todo mês.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="award-family"
                className="mt-1"
                checked={family === 'direta'}
                onChange={() => setFamily('direta')}
              />
              <span>
                Direta
                <span className="block text-xs text-texto-suave">
                  Valor por faixa de placas ativadas no mês — contrato novo com o primeiro
                  boleto pago dentro do prazo.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="award-family"
                className="mt-1"
                checked={family === 'antecipada'}
                onChange={() => {
                  setFamily('antecipada');
                  /*
                   * A régua do plano de carreira vem PREDEFINIDA (15/10/2026):
                   * as 41 faixas e o prazo de 45 dias. Só preenche a lista vazia —
                   * faixa que alguém já digitou não é apagada por um clique.
                   */
                  if (tiers.length === 0) {
                    setTiers(
                      CAREER_PLAN_TIERS.map((t) => ({
                        minPlates: String(t.minPlates),
                        amount: t.amount.replace('.', ','),
                      })),
                    );
                  }
                  setUseDeadline(true);
                  setDeadline('45');
                }}
              />
              <span>
                Antecipada Plano de Carreira
                <span className="block text-xs text-texto-suave">
                  A mesma régua da direta, somando as vendas do consultor e de toda a
                  hierarquia abaixo dele — o líder recebe pela faixa do time inteiro.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="award-family"
                className="mt-1"
                checked={family === 'produto'}
                onChange={() => setFamily('produto')}
              />
              <span>
                Premiação por produtos em placa
                <span className="block text-xs text-texto-suave">
                  Um valor fixo por veículo com boleto pago no mês, conforme os produtos
                  que ele tem — Carro Reserva, Assistência Profissional. Sem faixa.
                </span>
              </span>
            </label>
          </div>

          {/*
           * DE QUEM são os boletos — só na recorrência, que é a única família
           * com base de líder. Radio pelo mesmo motivo da família: as duas são
           * excludentes, e o banco guarda uma.
           */}
          {family === 'recorrencia' && (
            <fieldset className="mb-4 flex flex-col gap-2">
              <legend className="mb-1 text-xs font-medium text-texto">
                De quem são os boletos
              </legend>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="award-basis"
                  className="mt-1"
                  checked={basisSource === 'proprio'}
                  onChange={() => setBasisSource('proprio')}
                />
                <span>
                  Da carteira do consultor
                  <span className="block text-xs text-texto-suave">
                    Os veículos dele mesmo.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="award-basis"
                  className="mt-1"
                  checked={basisSource === 'hierarquia'}
                  onChange={() => setBasisSource('hierarquia')}
                />
                <span>
                  Dos liderados diretos — premiação de líder
                  <span className="block text-xs text-texto-suave">
                    Os veículos de quem responde diretamente a ele na hierarquia, um nível
                    só. É a única premiação que o líder pode repartir, pelo Painel do
                    time.
                  </span>
                </span>
              </label>
            </fieldset>
          )}

          <div className="grid grid-cols-1 items-start gap-x-4 gap-y-6 sm:grid-cols-2">
            {/*
             * O `className` do `Field` vai para o INPUT, não para o invólucro — foi
             * assim que a primeira tentativa de `col-span` aqui não fez nada. Quem
             * ocupa a linha inteira é uma `<div>` em volta.
             */}
            <div className="sm:col-span-2">
              <Field
                label="Título"
                help="Aparece no demonstrativo de cada consultor."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            {family === 'recorrencia' ? (
              <div className="flex flex-col gap-2">
                <Field
                  label="Percentual"
                  help="Aceita quebrado: 7,5 é 7,5%."
                  error={errors.percentage}
                  inputMode="decimal"
                  value={percentage}
                  /*
                   * O "%" e as letras não CHEGAM ao estado — são descartados na
                   * digitação, e não recusados no salvar.
                   *
                   * Era o defeito relatado: alguém escrevia `10%`, o campo aceitava,
                   * e a recusa vinha numa faixa no topo da tela sem dizer qual campo.
                   * Filtrar aqui faz o caractere simplesmente não aparecer, que é a
                   * forma de impedimento que não precisa de mensagem nenhuma.
                   *
                   * O que sobra é dígito, vírgula e ponto — e a validação continua
                   * existindo para o que passa por este filtro e ainda assim não é
                   * número, como `1,2,3`, e para 0 ou 101.
                   */
                  onChange={(e) => setPercentage(e.target.value.replace(/[^\d.,]/g, ''))}
                  required
                />
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={fromSecondBoleto}
                    onChange={(e) => setFromSecondBoleto(e.target.checked)}
                  />
                  <span>
                    Contar só a partir do 2º boleto?
                    <span className="block text-xs text-texto-suave">
                      Marcada, o 1º boleto de cada veículo não entra (o de troca entra
                      desde o 1º). Desmarcada, todo boleto pago dos tipos abaixo entra,
                      inclusive o 1º.
                    </span>
                  </span>
                </label>
              </div>
            ) : family === 'produto' ? null : (
              /*
               * A CAIXA e o campo moram juntos, e o campo só existe com ela
               * marcada. Duas razões, e a segunda é a que importa: um campo
               * desabilitado ao lado de uma caixa desmarcada deixa o número
               * visível e faz parecer que ele ainda vale; e o erro de
               * preenchimento precisa de um campo na tela onde ser consertado.
               */
              <div className="flex flex-col gap-2">
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={useDeadline}
                    onChange={(e) => setUseDeadline(e.target.checked)}
                  />
                  <span>
                    Considerar regra de 45 dias?
                    <span className="block text-xs text-texto-suave">
                      Marcada, a placa só conta se o 1º boleto for pago dentro do prazo
                      abaixo, contado do contrato. Desmarcada, ela conta com o 1º boleto
                      pago em <strong>qualquer</strong> data — e a competência nunca
                      fecha.
                    </span>
                  </span>
                </label>

                {useDeadline && (
                  <Field
                    label="Prazo de ativação"
                    help="Dias a partir do contrato para o 1º boleto ser pago. São 45 hoje."
                    error={errors.deadline}
                    inputMode="numeric"
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value.replace(/\D/g, ''))}
                    required
                  />
                )}
              </div>
            )}

            <Select
              label="Categoria"
              help="O lote de pagamento, não o cálculo."
              value={category}
              onChange={(e) => setCategory(e.target.value as '1x' | '2x')}
            >
              <option value="1x">1x</option>
              <option value="2x">2x</option>
            </Select>

            {/*
             * O começo é cobrado e o fim não — e o `required` acompanha isso.
             * Um asterisco num campo que pode ficar em branco é a tela pedindo
             * o que ela mesma não exige, e foi o que o `required` fixo nos dois
             * fazia até 15/09.
             */}
            <DateField
              layer={dateLayer}
              label="Contratos de"
              help="A data do contrato do veículo."
              error={errors.from}
              value={from}
              onChange={setFrom}
              required
            />
            <DateField
              layer={dateLayer}
              label="Contratos até"
              help="Em branco: sem limite — alcança o que ainda vai ser vendido."
              error={errors.until}
              value={until}
              onChange={setUntil}
            />
          </div>

          <div className="mt-6">
            <BoletoTypePicker selected={types} onChange={setTypes} />
            <p className="mt-2 text-xs text-texto-suave">
              {countsActivations(family)
                ? 'Escolha os tipos de PRIMEIRA MENSALIDADE: o veículo só ativa com o primeiro boleto pago de um deles. Uma mensalidade comum paga não ativa.'
                : 'Quais boletos contam como PERDA é outra régua, e ela é da base: Configurações › Comercial.'}
            </p>
          </div>

          {family === 'produto' && (
            <ProductRules
              products={products}
              onProducts={setProducts}
              combination={combination}
              onCombination={setCombination}
              useDiscount={useDiscount}
              onUseDiscount={setUseDiscount}
              discountFrom={discountFrom}
              onDiscountFrom={setDiscountFrom}
              discountPercentage={discountPercentage}
              onDiscountPercentage={setDiscountPercentage}
              errors={errors}
              layer={dateLayer}
            />
          )}

          {countsActivations(family) && (
            <DirectRules
              tiers={tiers}
              onTiers={setTiers}
              vehicleKinds={vehicleKinds}
              onVehicleKinds={setVehicleKinds}
              adhesionKinds={adhesionKinds}
              onAdhesionKinds={setAdhesionKinds}
              options={filters}
              optionsFailure={filtersFailure}
            />
          )}
        </fieldset>

        {/*
         * Dois `fieldset` saíram daqui em 16/09/2026, e os dois eram a mesma
         * pergunta feita de duas formas.
         *
         * "Quem recebe esta premiação" oferecia `todos` × `selecionados` e uma
         * lista de consultores; "quais grupos enxergam" marcava caixas por
         * grupo. Com a premiação nascendo dentro de um consultor, o público é
         * quem abriu a tela, e quem enxerga o consultor enxerga as premiações
         * dele — as duas perguntas passaram a ter uma resposta só, e uma
         * pergunta com uma resposta só não é uma pergunta.
         */}

        {linked.length > 0 && (
          <fieldset className="flex flex-col gap-3">
            <legend className="mb-1 font-semibold text-texto">
              Aplicar também a quem já está vinculado?
            </legend>
            <span className="text-sm text-texto-suave">
              Marcados recebem a régua nova inteira — datas, tipos de boleto, produtos e
              valores —, e o que foi ajustado só para eles se perde. Desmarcados continuam
              com a régua de antes; o modelo novo vale para os próximos vínculos.
            </span>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="discreto"
                size="sm"
                onClick={() =>
                  setApplyTo(
                    applyTo.length === linked.length ? [] : linked.map((c) => c.awardId),
                  )
                }
              >
                {applyTo.length === linked.length ? 'Desmarcar todos' : 'Marcar todos'}
              </Button>
            </div>
            <ul className="flex flex-col gap-2">
              {linked.map((c) => (
                <li key={c.awardId}>
                  <label className="flex items-center gap-2 text-sm text-texto">
                    <input
                      type="checkbox"
                      checked={applyTo.includes(c.awardId)}
                      onChange={(e) =>
                        setApplyTo(
                          e.target.checked
                            ? [...applyTo, c.awardId]
                            : applyTo.filter((id) => id !== c.awardId),
                        )
                      }
                    />
                    {c.user.nome}
                    <span className="text-texto-suave">
                      · código {c.erpVolunteerCode}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        )}

        {refusal !== null && <Alert tone="erro">{refusal}</Alert>}

        {/*
         * Desabilitado enquanto houver erro de preenchimento: o caminho de
         * conserto é o campo marcado, não uma segunda recusa vinda do
         * servidor. `canSave` **não substitui** a validação de lá — o banco
         * continua sendo quem manda, e se as duas divergirem a tela mostra a
         * frase que veio de lá.
         */}
        {frame === 'modal' ? (
          <ModalActions>
            <Button variant="contorno" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                setRefusal(null);
                save.mutate();
              }}
              loading={save.isPending}
              disabled={!canSave}
            >
              Salvar
            </Button>
          </ModalActions>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => save.mutate()} disabled={save.isPending || !canSave}>
              Salvar
            </Button>
            <Button variant="contorno" onClick={onClose}>
              Cancelar
            </Button>
          </div>
        )}
      </div>
    </>
  );

  if (frame === 'modal') {
    return (
      <Modal
        open
        onOpenChange={(open) => !open && onClose()}
        title={heading}
        /*
         * `xl` desde 15/10/2026, a pedido do responsável: em `lg` (672px) o
         * formulário longo — régua, tipos de boleto, produtos e a lista de
         * vinculados — ficava espremido e com rolagem demais.
         */
        size="xl"
        /* Formulário longo: um clique fora apagaria o que foi digitado. */
        dismissOnOutsideClick={false}
      >
        {body}
      </Modal>
    );
  }

  return <Card title={heading}>{body}</Card>;
}

/** Uma faixa em edição — texto, porque é o que o campo devolve. */
interface TierDraft {
  minPlates: string;
  amount: string;
}

/**
 * A régua da premiação direta: as faixas e o que a contagem filtra.
 *
 * ## A tabela é editável linha a linha, e é o pedido literal
 *
 * "Poderia adicionar 60, 2200" — então acrescentar um degrau é um botão, e não
 * um deploy. Cada linha é um piso e um valor; a ordem na tela é a de
 * digitação, e quem ordena de verdade é o servidor ao devolver, do menor piso
 * para o maior.
 *
 * ## As opções vêm do ESPELHO, com a contagem ao lado
 *
 * Os códigos de tipo de veículo e de adesão são do fornecedor, e um deles não
 * tem sequer lista de referência. Oferecer caixas com o número de veículos de
 * cada uma faz duas coisas que um campo de texto não faria: impede o código
 * digitado errado, e mostra o tamanho do que se está marcando — inclusive o
 * tipo de adesão `0`, com 20.035 veículos e nenhuma descrição, que é a G13
 * aparecendo na tela onde ela é decidida.
 */
function DirectRules({
  tiers,
  onTiers,
  vehicleKinds,
  onVehicleKinds,
  adhesionKinds,
  onAdhesionKinds,
  options,
  optionsFailure,
}: {
  tiers: TierDraft[];
  onTiers: (tiers: TierDraft[]) => void;
  vehicleKinds: string[];
  onVehicleKinds: (codes: string[]) => void;
  adhesionKinds: string[];
  onAdhesionKinds: (codes: string[]) => void;
  options: CatalogVehicleFilters | null;
  optionsFailure: string | null;
}) {
  const toggle = (codes: string[], code: string, on: boolean) =>
    on ? [...codes, code] : codes.filter((c) => c !== code);

  return (
    <div className="mt-6 flex flex-col gap-6">
      <div className="flex flex-col">
        <span className="rotulo mb-2">Quanto paga, por faixa de placas</span>

        {tiers.length === 0 ? (
          /*
           * Régua vazia não é um detalhe a preencher depois: é a premiação que
           * aparece no demonstrativo e não paga nada. O aviso é do mesmo tom do
           * "sem tipo de veículo" do cartão.
           */
          <p className="mb-2 text-xs text-alerta">
            Sem nenhuma faixa, esta premiação não apura — ela aparece no demonstrativo
            dizendo que falta a régua.
          </p>
        ) : (
          <div className="mb-2 flex flex-col gap-2">
            {tiers.map((tier, index) => (
              <div key={index} className="flex flex-wrap items-end gap-3">
                <Field
                  label="A partir de"
                  help="placas ativadas no mês"
                  className="max-w-[7rem]"
                  inputMode="numeric"
                  value={tier.minPlates}
                  onChange={(e) =>
                    onTiers(
                      tiers.map((t, i) =>
                        i === index
                          ? { ...t, minPlates: e.target.value.replace(/\D/g, '') }
                          : t,
                      ),
                    )
                  }
                />
                <Field
                  label="Paga"
                  help="o valor cheio da faixa, não por placa"
                  className="max-w-[10rem]"
                  inputMode="decimal"
                  value={tier.amount}
                  onChange={(e) =>
                    onTiers(
                      tiers.map((t, i) =>
                        i === index
                          ? { ...t, amount: e.target.value.replace(/[^\d.,]/g, '') }
                          : t,
                      ),
                    )
                  }
                />
                <Button
                  variant="contorno"
                  size="sm"
                  icon="fechar"
                  className="mb-1"
                  onClick={() => onTiers(tiers.filter((_, i) => i !== index))}
                >
                  Remover
                </Button>
              </div>
            ))}
          </div>
        )}

        <div>
          <Button
            variant="contorno"
            size="sm"
            icon="mais"
            onClick={() => onTiers([...tiers, { minPlates: '', amount: '' }])}
          >
            Acrescentar faixa
          </Button>
        </div>

        <p className="mt-3 text-xs text-texto-suave">
          Vale a maior faixa alcançada: com 3, 5 e 10 cadastradas, quem ativou 7 placas
          recebe a de 5. Abaixo da menor não há faixa — e isso é diferente de uma faixa de
          R$ 0,00.
        </p>
      </div>

      {optionsFailure !== null ? (
        <NotProvided>
          {optionsFailure} Escolher o que a premiação conta exige também a permissão de
          ver o cadastro do ERP.
        </NotProvided>
      ) : options === null ? (
        <NotProvided>Carregando os tipos do cadastro do ERP…</NotProvided>
      ) : (
        <>
          <FilterChoice
            legend="Conta quais tipos de veículo"
            help="Carro e camionete/SUV são dois códigos; moto é outro. Nenhum marcado faz a premiação recusar a apuração."
            options={options.vehicleKinds}
            selected={vehicleKinds}
            onChange={(code, on) => onVehicleKinds(toggle(vehicleKinds, code, on))}
          />
          <FilterChoice
            legend="Conta quais tipos de adesão"
            help="É como o ERP responde “venda nova ou troca”. Nenhum marcado faz a premiação recusar a apuração — e marcar tudo inclui a troca."
            options={options.adhesionKinds}
            selected={adhesionKinds}
            onChange={(code, on) => onAdhesionKinds(toggle(adhesionKinds, code, on))}
          />
        </>
      )}
    </div>
  );
}

/** O menor e o maior valor, como texto de `numeric(12,2)`. */
function minOf(values: string[]): string {
  return values.reduce((a, b) => (Number(b) < Number(a) ? b : a));
}
function maxOf(values: string[]): string {
  return values.reduce((a, b) => (Number(b) > Number(a) ? b : a));
}

/** `AAAA-MM-DD` → `DD/MM/AAAA`, sem fuso: é data de negócio. */
function formatDay(day: string): string {
  const [y, m, d] = day.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * A régua da premiação POR PRODUTO (14/10/2026): os produtos marcados na
 * tabela do cadastro, cada um com quanto paga por veículo (`ProductPicker`), e
 * as duas escolhas que as regras da casa pedem — somar ou pagar o maior, e o
 * desconto.
 */
function ProductRules({
  products,
  onProducts,
  combination,
  onCombination,
  useDiscount,
  onUseDiscount,
  discountFrom,
  onDiscountFrom,
  discountPercentage,
  onDiscountPercentage,
  errors,
  layer,
}: {
  /** A camada do calendário — `modal` quando o formulário abre em modal. */
  layer: 'painel' | 'modal' | undefined;
  products: ProductChoice[];
  onProducts: (products: ProductChoice[]) => void;
  combination: AwardProductCombination;
  onCombination: (c: AwardProductCombination) => void;
  useDiscount: boolean;
  onUseDiscount: (on: boolean) => void;
  discountFrom: string;
  onDiscountFrom: (v: string) => void;
  discountPercentage: string;
  onDiscountPercentage: (v: string) => void;
  errors: Record<string, string>;
}) {
  return (
    <div className="mt-6 flex flex-col gap-6">
      <ProductPicker selected={products} onChange={onProducts} error={errors.products} />

      <fieldset className="flex flex-col gap-2">
        <legend className="rotulo mb-2">Veículo com mais de um produto marcado</legend>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="award-product-combination"
            className="mt-1"
            checked={combination === 'soma'}
            onChange={() => onCombination('soma')}
          />
          <span>
            Soma os produtos
            <span className="block text-xs text-texto-suave">
              Assistência Profissional 5 e Benefício Gard no mesmo carro pagam os dois.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="award-product-combination"
            className="mt-1"
            checked={combination === 'maior'}
            onChange={() => onCombination('maior')}
          />
          <span>
            Paga uma vez, o de maior valor
            <span className="block text-xs text-texto-suave">
              Auxílio Gard e Carro Reserva no mesmo carro pagam R$ 3,00, e não R$ 6,00.
            </span>
          </span>
        </label>
      </fieldset>

      <div className="flex flex-col gap-2">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={useDiscount}
            onChange={(e) => onUseDiscount(e.target.checked)}
          />
          <span>
            Boleto pago com desconto reduz o valor?
            <span className="block text-xs text-texto-suave">
              Pago com desconto é o valor pago menor que o do boleto. A redução vale para
              os contratos a partir da data abaixo; os anteriores recebem o valor
              integral.
            </span>
          </span>
        </label>
        {useDiscount && (
          <div className="grid grid-cols-1 items-start gap-x-4 gap-y-6 sm:grid-cols-2">
            <DateField
              layer={layer}
              label="Contratos a partir de"
              help="“Após 31/07/2025” é 01/08/2025."
              error={errors.discountFrom}
              value={discountFrom}
              onChange={onDiscountFrom}
              required
            />
            <Field
              label="Tira quanto do produto (%)"
              help="20 é 20%: R$ 10,00 vira R$ 8,00."
              error={errors.discountPercentage}
              inputMode="decimal"
              value={discountPercentage}
              onChange={(e) =>
                onDiscountPercentage(e.target.value.replace(/[^\d.,]/g, ''))
              }
              required
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Uma lista de códigos do ERP para marcar, com a contagem de cada um.
 *
 * O número ao lado não é enfeite: é ele que mostra que marcar o código sem
 * descrição significa incluir 20.035 veículos, e que deixá-lo de fora significa
 * excluí-los. Sem ele, as duas decisões pareceriam do mesmo tamanho.
 */
function FilterChoice({
  legend,
  help,
  options,
  selected,
  onChange,
}: {
  legend: string;
  help: string;
  options: CatalogVehicleFilter[];
  selected: string[];
  onChange: (code: string, on: boolean) => void;
}) {
  return (
    <fieldset className="flex flex-col">
      <legend className="rotulo mb-2">{legend}</legend>
      {options.length === 0 ? (
        <NotProvided>
          O espelho desta base não tem veículo nenhum — sincronize o cadastro do ERP antes
          de cadastrar a régua.
        </NotProvided>
      ) : (
        <div className="flex flex-col gap-2">
          {options.map((o) => (
            <label key={o.code} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={selected.includes(o.code)}
                onChange={(e) => onChange(o.code, e.target.checked)}
              />
              <span>
                {o.descricao ?? 'sem descrição no ERP'}
                <span className="block text-xs text-texto-suave">
                  {'código ' +
                    o.code +
                    ' · ' +
                    o.vehicles.toLocaleString('pt-BR') +
                    ' veículos'}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}
      <p className="mt-3 text-xs text-texto-suave">{help}</p>
    </fieldset>
  );
}

/* --- `ConsultantPicker` SAIU em 16/09/2026 -------------------------------- *
 *
 * Era a busca de quem recebe a premiação: duas áreas — os já escolhidos em
 * cima, os demais embaixo —, `SEARCH_LIMIT` para a lista não virar rolagem
 * longa, e teto de altura para o cartão ficar do mesmo tamanho com três pessoas
 * e com trezentas.
 *
 * Saiu inteiro com a pergunta que respondia: a premiação passou a pertencer ao
 * consultor em cuja tela ela é cadastrada, e um seletor aqui faria a tela do
 * consultor A poder gravar a premiação do B — que é a tela antiga desenhada
 * dentro da nova.
 * ------------------------------------------------------------------------- */
